import { useEffect, useState } from "react";
import { School } from "../../interfaces/school";
import { useNavigate, useParams } from "react-router-dom";
import { getStates } from "../../services/helpers";
import {
  deleteSchool,
  getSchool,
  updateSchool,
} from "../../services/schoolService";
import ConfirmDelete from "../../components/component-tools/ConfirmDelete";
import ToolTip from "../../components/component-tools/ToolTip";

const ViewSchool = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [school, setSchool] = useState<School>();
  const { schoolID } = useParams();
  const states = getStates();
  const [editing, setEditing] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    const fetchSchoolData = async () => {
      try {
        setLoading(true);
        if (schoolID) {
          const numericSchoolID = parseInt(schoolID, 10);
          const schoolResponse = await getSchool(numericSchoolID);
          setSchool(schoolResponse);
        }
      } catch {
        console.log("Error fetching school");
      } finally {
        setLoading(false);
      }
    };
    fetchSchoolData();
  }, [schoolID]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    field: keyof School,
  ) => {
    setEditing(true);
    let value: string | boolean =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    if (school) {
      setSchool({
        ...school,
        [field]: value,
      });
    }
  };

  const handleUpdateSchool = async () => {
    setLoadingSpinner(true);
    try {
      if (school) {
        await updateSchool(school);
        setEditing(false);
        setSuccessMessage("School updated successfully.");
      }
    } catch (error) {
      console.error("Error updating:", error);
    } finally {
      setLoadingSpinner(false);
    }
  };

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleDeleteSchool = async () => {
    if (!school || !school.schoolID) {
      setErrorMessage("Invalid school selection.");
      return;
    }

    setLoadingSpinner(true);
    try {
      await deleteSchool(school.schoolID);
      navigate("/admin/master-lists/schools");
    } catch (error) {
      console.error("Error deleting school:", error);
      setErrorMessage("Failed to delete school. Please try again.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  return (
    <div className="content-container">
      {!loading && (
        <div className="header-container">
          <div
            className="page-items-container"
            style={{ alignItems: "center" }}
          >
            <h1>{school?.name}</h1>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>
          <div className="data-container">
            <div className="field-stacked" style={{ flex: "0 0 50%" }}>
              <label className="field-label">Name:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={school?.name || ""}
                  onChange={(e) => handleInputChange(e, "name")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 50%" }}>
              <label className="field-label">Contact:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={school?.contact || ""}
                  onChange={(e) => handleInputChange(e, "contact")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 50%" }}>
              <label className="field-label">Greeting:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={school?.greeting || ""}
                  onChange={(e) => handleInputChange(e, "greeting")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 40%" }}></div>
            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Title:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={school?.title || ""}
                  onChange={(e) => handleInputChange(e, "title")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 60%" }}></div>
            <div className="field-stacked" style={{ flex: "0 0 25%" }}>
              <label className="field-label">Address:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={school?.address || ""}
                  onChange={(e) => handleInputChange(e, "address")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 15%" }}>
              <label className="field-label">Address 2:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={school?.address2 || ""}
                  onChange={(e) => handleInputChange(e, "address2")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 15%" }}>
              <label className="field-label">City:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={school?.city || ""}
                  onChange={(e) => handleInputChange(e, "city")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 15%" }}>
              <label className="field-label">State:</label>
              <select
                className="form-select"
                value={school?.state || ""}
                onChange={(e) => handleInputChange(e, "state")}
              >
                <option value="">Select State</option>
                {states.map((state) => (
                  <option key={state.abbreviation} value={state.abbreviation}>
                    {state.name} ({state.abbreviation})
                  </option>
                ))}
              </select>
            </div>
            {school?.state === "INT" && (
              <div className="field-stacked" style={{ flex: "0 0 15%" }}>
                <label className="field-label">Country:</label>
                <div className="field-input">
                  <input
                    className="form-control"
                    type="text"
                    value={school?.country || ""}
                    onChange={(e) => handleInputChange(e, "country")}
                  />
                </div>
              </div>
            )}
            <div className="field-stacked" style={{ flex: "0 0 15%" }}>
              <label className="field-label">ZipCode:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={school?.zipCode || ""}
                  onChange={(e) => handleInputChange(e, "zipCode")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 40%" }}>
              <label className="field-label">Email:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={school?.email || ""}
                  onChange={(e) => handleInputChange(e, "email")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">Phone:</label>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={school?.phone || ""}
                  onChange={(e) => handleInputChange(e, "phone")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <div style={{ display: "flex", gap: "5px" }}>
                <label className="field-label">Send Certified:</label>
                <ToolTip
                  tooltipText="Controls if csv box is visible on the reference tracking page once used to build a reference."
                  placement="top"
                  className="info-icon-spec"
                ></ToolTip>
              </div>
              <div>
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={
                    school?.sendCertified ? school?.sendCertified : false
                  }
                  onChange={(e) => handleInputChange(e, "sendCertified")}
                />
              </div>
            </div>
            {/* <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <div style={{ display: "flex", gap: "5px" }}>
                <label className="field-label">Verified:</label>
              </div>
              <div>
                <input
                  className={`form-check-input`}
                  type="checkbox"
                  checked={school?.verified}
                  onChange={(e) => handleInputChange(e, "verified")}
                />
              </div>
            </div> */}
            <div className="field-stacked" style={{ flex: "0 0 100%" }}>
              <label className="field-label">Comments:</label>
              <div className="field-input">
                <textarea
                  className="form-control"
                  value={school?.comments || ""}
                  onChange={(e) => handleInputChange(e, "comments")}
                  style={{ height: "100px" }}
                  maxLength={4000}
                />
                <div
                  style={{
                    fontSize: "14px",
                    marginTop: "4px",
                    textAlign: "right",
                  }}
                >
                  {school?.comments?.length ?? 0}/4000
                </div>
              </div>
            </div>

            <div
              style={{
                flex: "0 0 100%",
                alignSelf: "end",
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                gap: "20px",
              }}
            >
              <button
                className={`oxbtn btn-green ${!editing ? "disabled" : ""}`}
                onClick={handleUpdateSchool}
                disabled={!editing}
              >
                Save
              </button>
              <div style={{ flex: "0 0 10%" }}>
                <button
                  className="oxbtn btn-red"
                  onClick={() => setShowConfirmation(true)}
                >
                  Delete
                </button>
              </div>
              {showConfirmation && (
                <ConfirmDelete
                  onConfirm={() => handleDeleteSchool()}
                  onCancel={() => setShowConfirmation(false)}
                  message="Are you sure you want to delete this school?"
                />
              )}
            </div>
          </div>
        </>
      )}

      {successMessage && (
        <div className="alert-message alert alert-success alert-dismissible fade show">
          {successMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccessMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}

      {errorMessage && (
        <div
          className="alert-message alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {errorMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setErrorMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}

      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default ViewSchool;
